import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import { 
  fetchOrganizationById,
  fetchOrganizationUsers,
  activateOrganization,
  deactivateOrganization,
  deleteOrganization,
  setSelectedOrganization,
} from '../redux/reducers/organizationsSlice'
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Calendar,
  Clock,
  Users,
  GitBranch,
  Settings,
  Activity,
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Power,
  UserPlus,
  Download,
  Upload,
  Shield,
  Crown,
  AlertTriangle,
  Eye,
  MoreVertical,
  Plus,
  ChevronDown,
  Filter,
  Search,
} from 'lucide-react'
import { format, formatDistanceToNow } from 'date-fns'

const OrganizationDetails: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  
  const { 
    selectedOrganization,
    loading,
    error 
  } = useAppSelector((state) => state.organizations)
  
  const [activeTab, setActiveTab] = useState('overview')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [organizationUsers, setOrganizationUsers] = useState<any[]>([])
  const [organizationBranches, setOrganizationBranches] = useState<any[]>([])
  const [activityLogs, setActivityLogs] = useState<any[]>([])
  const [usersPage, setUsersPage] = useState(1)
  const [usersPageSize, setUsersPageSize] = useState(10)
  const [usersTotalCount, setUsersTotalCount] = useState(0)
  
  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationById(organizationId))
    }
    
    return () => {
      dispatch(setSelectedOrganization(null))
    }
  }, [dispatch, organizationId])
  
  useEffect(() => {
    if (organizationId && activeTab === 'users') {
      dispatch(fetchOrganizationUsers({ 
        organizationId, 
        page: usersPage, 
        pageSize: usersPageSize 
      })).then((result: any) => {
        if (result.payload) {
          setOrganizationUsers(result.payload.data || [])
          setUsersTotalCount(result.payload.total || 0)
        }
      })
    }
  }, [dispatch, organizationId, activeTab, usersPage, usersPageSize])
  
  // Set branches from selectedOrganization
  useEffect(() => {
    if (selectedOrganization?.branches) {
      setOrganizationBranches(selectedOrganization.branches)
    }
  }, [selectedOrganization])
  
  const handleActivate = async () => {
    if (organizationId) {
      await dispatch(activateOrganization(organizationId))
      dispatch(fetchOrganizationById(organizationId))
    }
  }
  
  const handleDeactivate = async () => {
    if (organizationId) {
      await dispatch(deactivateOrganization(organizationId))
      dispatch(fetchOrganizationById(organizationId))
    }
  }
  
  // TODO: Delete organization functionality - Not ready for production
  // const handleDelete = async () => {
  //   if (organizationId) {
  //     await dispatch(deleteOrganization(organizationId))
  //     navigate('/organizations')
  //   }
  // }
  
  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200">
        <XCircle className="w-3 h-3 mr-1" />
        Inactive
      </span>
    )
  }
  
  const getRoleBadge = (role: string) => {
    const roleColors: Record<string, string> = {
      'Owner': 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
      'Admin': 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
      'Manager': 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200',
      'Member': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    }
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleColors[role] || roleColors['Member']}`}>
        {role === 'Owner' && <Crown className="w-3 h-3 mr-1" />}
        {role}
      </span>
    )
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertTriangle className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-lg font-medium text-foreground">Error loading organization</p>
        <p className="text-sm text-muted-foreground mt-1">{error}</p>
        <button 
          onClick={() => navigate('/organizations')}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          Back to Organizations
        </button>
      </div>
    )
  }
  
  if (!selectedOrganization) {
    return null
  }
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/organizations')}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Building2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-foreground">{selectedOrganization.name}</h1>
                <p className="text-sm text-muted-foreground">Organization ID: {selectedOrganization.uuid}</p>
              </div>
            </div>
            <div className="ml-4">
              {getStatusBadge(selectedOrganization.isActive)}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors flex items-center space-x-2">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors flex items-center space-x-2">
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </button>
            {selectedOrganization.isActive ? (
              <button 
                disabled
                className="px-3 py-1.5 text-sm border border-orange-500 text-orange-500 rounded-lg opacity-50 cursor-not-allowed flex items-center space-x-2"
              >
                <Power className="w-4 h-4" />
                <span>Deactivate</span>
              </button>
            ) : (
              <button 
                disabled
                className="px-3 py-1.5 text-sm border border-green-500 text-green-500 rounded-lg opacity-50 cursor-not-allowed flex items-center space-x-2"
              >
                <Power className="w-4 h-4" />
                <span>Activate</span>
              </button>
            )}
            {/* Delete button - Hidden until ready for production
            <button
              disabled
              className="px-3 py-1.5 text-sm border border-red-500 text-red-500 rounded-lg opacity-50 cursor-not-allowed flex items-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </button>
            */}
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Dialog - Hidden until ready for production
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-foreground mb-2">Delete Organization</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Are you sure you want to delete <strong>{selectedOrganization.name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete Organization
              </button>
            </div>
          </div>
        </div>
      )}
      */}
      
      {/* Tabs */}
      <div className="flex border-b border-border bg-muted/30">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'overview' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Building2 className="w-4 h-4" />
          <span>Overview</span>
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'users' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Users</span>
        </button>
        <button
          onClick={() => setActiveTab('branches')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'branches' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <GitBranch className="w-4 h-4" />
          <span>Branches</span>
        </button>
        <button
          onClick={() => setActiveTab('settings')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'settings' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Settings className="w-4 h-4" />
          <span>Settings</span>
        </button>
        <button
          onClick={() => setActiveTab('activity')}
          className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium transition-all ${
            activeTab === 'activity' 
              ? 'text-foreground border-b-2 border-primary bg-card' 
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
        >
          <Activity className="w-4 h-4" />
          <span>Activity</span>
        </button>
      </div>
      
      {/* Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'overview' ? (
          <div className="space-y-6 max-w-7xl mx-auto">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Total Users</p>
                    <p className="text-2xl font-bold text-foreground mt-1">
                      {selectedOrganization.organizationUsers?.length || 0}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-primary opacity-20" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Branches</p>
                    <p className="text-2xl font-bold text-foreground mt-1">
                      {organizationBranches.length}
                    </p>
                  </div>
                  <GitBranch className="w-8 h-8 text-blue-600 dark:text-blue-400 opacity-20" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Active Since</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {format(new Date(selectedOrganization.createdAt), 'MMM yyyy')}
                    </p>
                  </div>
                  <Calendar className="w-8 h-8 text-green-600 dark:text-green-400 opacity-20" />
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase">Last Activity</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {formatDistanceToNow(new Date(selectedOrganization.updatedAt), { addSuffix: true })}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-purple-600 dark:text-purple-400 opacity-20" />
                </div>
              </div>
            </div>
            
            {/* Organization Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Building2 className="w-5 h-5 mr-2" />
                Organization Information
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Organization Name</p>
                  <p className="text-sm font-medium text-foreground">{selectedOrganization.name}</p>
                </div>
                {selectedOrganization.description && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Description</p>
                    <p className="text-sm font-medium text-foreground">{selectedOrganization.description}</p>
                  </div>
                )}
                {selectedOrganization.email && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Email</p>
                    <p className="text-sm font-medium text-foreground flex items-center">
                      <Mail className="w-3 h-3 mr-1 text-muted-foreground" />
                      {selectedOrganization.email}
                    </p>
                  </div>
                )}
                {selectedOrganization.phone && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Phone</p>
                    <p className="text-sm font-medium text-foreground flex items-center">
                      <Phone className="w-3 h-3 mr-1 text-muted-foreground" />
                      {selectedOrganization.phone}
                    </p>
                  </div>
                )}
                {selectedOrganization.website && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">Website</p>
                    <p className="text-sm font-medium text-foreground flex items-center">
                      <Globe className="w-3 h-3 mr-1 text-muted-foreground" />
                      {selectedOrganization.website}
                    </p>
                  </div>
                )}
                {selectedOrganization.address && (
                  <div className="col-span-3">
                    <p className="text-xs text-muted-foreground uppercase mb-1">Address</p>
                    <p className="text-sm font-medium text-foreground flex items-start">
                      <MapPin className="w-3 h-3 mr-1 text-muted-foreground mt-0.5" />
                      <span>
                        {selectedOrganization.address}
                        {selectedOrganization.city && `, ${selectedOrganization.city}`}
                        {selectedOrganization.state && `, ${selectedOrganization.state}`}
                        {selectedOrganization.country && `, ${selectedOrganization.country}`}
                        {selectedOrganization.postalCode && ` ${selectedOrganization.postalCode}`}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* System Information */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2" />
                System Information
              </h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Organization ID</p>
                  <p className="text-sm font-mono text-foreground">{selectedOrganization.uuid}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedOrganization.isActive)}</div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Provider</p>
                  <p className="text-sm font-medium text-foreground">
                    {selectedOrganization.provider || 'Internal'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Created Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {format(new Date(selectedOrganization.createdAt), 'PPP')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-1">Last Updated</p>
                  <p className="text-sm font-medium text-foreground">
                    {format(new Date(selectedOrganization.updatedAt), 'PPP HH:mm')}
                  </p>
                </div>
                {selectedOrganization.externalId && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase mb-1">External ID</p>
                    <p className="text-sm font-mono text-foreground">{selectedOrganization.externalId}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'users' ? (
          <div className="max-w-7xl mx-auto">
            {/* Users Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Organization Users</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage users and their roles within the organization
                </p>
              </div>
              <button 
                onClick={() => setShowAddUserModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                <span>Add User</span>
              </button>
            </div>
            
            {/* Users Table */}
            {organizationUsers.length > 0 ? (
              <div>
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Role
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Branch
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Joined
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {organizationUsers.map((orgUser) => (
                      <tr key={`${orgUser.userId}-${orgUser.organizationId}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">
                                {orgUser.user?.name?.charAt(0) || orgUser.user?.email?.charAt(0) || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {orgUser.user?.name || orgUser.name || 'Unknown User'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {orgUser.user?.email || orgUser.email || 'No email'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getRoleBadge(orgUser.role?.name || 'Member')}
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-foreground">
                            {orgUser.branch?.name || 'Main'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-foreground">
                            {orgUser.createdAt ? format(new Date(orgUser.createdAt), 'MMM d, yyyy') : 'Unknown'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            orgUser.isActive 
                              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                          }`}>
                            {orgUser.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button className="p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors" title="View User">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 text-muted-foreground hover:bg-muted rounded-lg transition-colors" title="Edit Role">
                              <Edit className="w-4 h-4" />
                            </button>
                            <button className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition-colors" title="Remove User">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Pagination Controls */}
                {usersTotalCount > usersPageSize && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <div className="text-sm text-muted-foreground">
                      Showing {((usersPage - 1) * usersPageSize) + 1} to {Math.min(usersPage * usersPageSize, usersTotalCount)} of {usersTotalCount} users
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setUsersPage(usersPage - 1)}
                        disabled={usersPage === 1}
                        className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Previous
                      </button>
                      <span className="px-3 py-1.5 text-sm">
                        Page {usersPage} of {Math.ceil(usersTotalCount / usersPageSize)}
                      </span>
                      <button
                        onClick={() => setUsersPage(usersPage + 1)}
                        disabled={usersPage >= Math.ceil(usersTotalCount / usersPageSize)}
                        className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <Users className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No users found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add users to this organization to get started
                </p>
                <button 
                  onClick={() => setShowAddUserModal(true)}
                  className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Add First User
                </button>
              </div>
            )}
          </div>
        ) : activeTab === 'branches' ? (
          <div className="max-w-7xl mx-auto">
            {/* Branches Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Organization Branches</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Manage branch locations for the organization
                </p>
              </div>
              <button 
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Branch</span>
              </button>
            </div>
            
            {/* Branches List */}
            {organizationBranches && organizationBranches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {organizationBranches.map((branch: any) => (
                  <div key={branch.id || branch.uuid} className="bg-card border border-border rounded-lg p-4 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <GitBranch className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-foreground">{branch.name}</h3>
                      </div>
                      <button className="text-muted-foreground hover:text-foreground">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                    
                    {branch.address && (
                      <div className="mb-2">
                        <div className="flex items-start space-x-2 text-sm text-muted-foreground">
                          <MapPin className="w-4 h-4 mt-0.5" />
                          <span>
                            {branch.address}
                            {branch.city && `, ${branch.city}`}
                            {branch.state && `, ${branch.state}`}
                            {branch.country && `, ${branch.country}`}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {branch.phone && (
                      <div className="mb-2">
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <Phone className="w-4 h-4" />
                          <span>{branch.phone}</span>
                        </div>
                      </div>
                    )}
                    
                    {branch.email && (
                      <div className="mb-3">
                        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                          <Mail className="w-4 h-4" />
                          <span>{branch.email}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between pt-3 border-t border-border">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        branch.isActive !== false
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                      }`}>
                        {branch.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                      <div className="flex space-x-2">
                        <button className="text-sm text-primary hover:underline">Edit</button>
                        <button className="text-sm text-red-500 hover:underline">Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-card border border-border rounded-lg p-12 text-center">
                <GitBranch className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No branches configured</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add branches to organize users by location
                </p>
                <button className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                  Add First Branch
                </button>
              </div>
            )}
          </div>
        ) : activeTab === 'settings' ? (
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12">
              <Settings className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">Settings coming soon</p>
              <p className="text-sm text-muted-foreground mt-1">
                Organization settings and preferences will be available here
              </p>
            </div>
          </div>
        ) : activeTab === 'activity' ? (
          <div className="max-w-4xl mx-auto">
            <div className="text-center py-12">
              <Activity className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-lg font-medium text-muted-foreground">No activity logs available</p>
              <p className="text-sm text-muted-foreground mt-1">
                Organization activity will appear here once tracked
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OrganizationDetails